import {
	Alarm,
	Check,
	Lightning,
	MagicWand,
	ShieldWarning,
	Sparkle,
	UploadSimple,
	WarningCircle
} from "@phosphor-icons/react";
import {
	FIX_TYPE,
	getFixTypeFromFixableItems,
	TQuestionWithAnswer,
	useEDCFlow,
	VALIDATION_STATUS,
	ValidationResult
} from "@smvtech/x-flux";
import { AxiosError } from "axios";
import toast from "components/common/Toaster";
import useIsMobile from "pages/EDCFlow/hooks/useIsMobile";
import { useState } from "react";

type DocumentVerificationStatusProps = {
	validationResult: ValidationResult;
	document: TQuestionWithAnswer;
	timeSaved: number;
	reUploadInputId: string;
	isErrorMode?: boolean;
	onClose?: () => void;
	fixType?: FIX_TYPE;
	applicationId?: string;
	isDocumentsComplete?: boolean;
	onDocumentsCompleted?: () => void;
};

const DocumentVerificationStatus: React.FC<DocumentVerificationStatusProps> = ({
	validationResult,
	document,
	timeSaved,
	reUploadInputId,
	isErrorMode,
	onClose,
	fixType,
	applicationId,
	isDocumentsComplete,
	onDocumentsCompleted
}) => {
	const { applyAutoFix } = useEDCFlow();
	const [isFixing, setIsFixing] = useState(false);
	const answerId = document.answer_data?._id;

	const isMobile = useIsMobile();

	const { order } = useEDCFlow();

	const isDocumentLocked = !!order?.qc_terms_and_conditions;

	const handleDismissAndContinue = () => {
		onClose?.();
		if (!isDocumentsComplete) {
			return;
		}
		onDocumentsCompleted?.();
	};

	const renderMobile = () => {
		if (validationResult?.status === VALIDATION_STATUS.FIXABLE) {
			if (fixType === FIX_TYPE.AUTO) {
				return (
					<div className="w-full overflow-hidden rounded-lg">
						<div className="flex flex-col gap-4">
							<div>
								<p className="text-xl font-semibold">Fixable Issues Identified</p>
								<p className="text-xs text-gray-500">We found few fixable issues</p>
							</div>

							<div className="flex flex-wrap gap-2">
								{validationResult?.fixable?.items?.map((item) => (
									<div
										key={item?.validation_name}
										className="flex items-center gap-1 text-[#B54708] bg-[#FEF0C7] px-3 py-1.5 rounded-full text-[13px]">
										<WarningCircle size={14} />
										{item?.error_text}
									</div>
								))}
							</div>

							<div className="flex items-center gap-3 bg-[#FFFCF2] p-4 rounded-xl">
								<div className="flex items-center justify-center w-9 h-9 rounded-full bg-[#FEF0C7]">
									<Alarm size={18} className="text-[#B54708]" />
								</div>

								<p className="text-sm">
									Click Auto-Fix and <b>save 10 mins</b> of your time
								</p>
							</div>

							<button
								className={`flex w-full items-center justify-center gap-2
					bg-gradient-to-b from-[#F79009] to-[#DC6803]
					shadow-[0_0_0_1.2px_#DC6803]
					rounded-md text-sm px-3 py-3 text-white font-medium ${isDocumentLocked ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed" : ""}`}
								disabled={isFixing || isDocumentLocked}
								onClick={async () => {
									setIsFixing(true);
									try {
										if (!applicationId || !answerId) {
											toast.error("Missing application or answer ID. Cannot apply auto-fix.");
											return;
										}
										await applyAutoFix(applicationId, answerId);
									} catch (error) {
										toast.error(
											error instanceof AxiosError
												? error?.response?.data?.message
												: "Failed to fix document. Please try again."
										);
									} finally {
										setIsFixing(false);
									}
								}}>
								Auto-Fix <Sparkle size={16} />
							</button>
						</div>
					</div>
				);
			}

			return (
				<div className="w-full flex flex-col gap-5">
					<div>
						<p className="text-2xl font-bold text-gray-900">We’re reviewing this</p>
						<p className="text-sm text-gray-500 mt-1">We found few fixable issues</p>
					</div>

					<div className="flex flex-wrap gap-2">
						{validationResult.fixable.items.map((item) => (
							<span
								key={item.validation_name}
								className="flex items-center gap-1.5 text-[#B54708] bg-[#FEF0C7] px-3 py-1.5 rounded-full text-[13px] font-medium">
								<ShieldWarning size={14} />
								{item.error_text}
							</span>
						))}
					</div>

					<div>
						<p className="text-2xl font-bold text-gray-900 mb-2">What we are doing</p>
						<p className="text-sm text-gray-500 leading-relaxed">
							Our visa experts are attempting to enhance this image for the embassy. No immediate action
							required
						</p>
					</div>

					<div className="flex items-center bg-[#FFFCF2] p-4 gap-4 rounded-xl">
						<div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#FEF0C7] shrink-0">
							<Lightning size={20} weight="fill" className="text-[#F79009]" />
						</div>
						<p className="text-sm text-gray-700">
							For a <b>20% faster processing time</b>, we recommend re-uploading a clearer image
						</p>
					</div>

					<button
						disabled={isDocumentLocked}
						onClick={() => {
							const input = window.document.getElementById(reUploadInputId) as HTMLInputElement;
							input?.click();
						}}
						className={`flex w-full items-center justify-center gap-2 bg-gradient-to-b from-[#4E45C4] to-[#3730A3] shadow-[0_0_0_1.2px_#4E45C4] rounded-xl text-sm font-medium px-3 py-4 text-white ${isDocumentLocked ? "opacity-50 cursor-not-allowed" : ""}`}>
						Re-Upload <UploadSimple size={16} />
					</button>

					<button
						className="w-full text-center font-semibold text-sm text-gray-900"
						onClick={handleDismissAndContinue}>
						Dismiss & Continue Anyway
					</button>
				</div>
			);
		}

		if (validationResult?.status === VALIDATION_STATUS.UNFIXABLE) {
			return (
				<div className="w-full overflow-hidden rounded-lg p-4 flex flex-col gap-5">
					<div>
						<p className="text-xl font-semibold text-gray-900">We have identified unfixable issues</p>
						<p className="text-sm text-gray-500 mt-1">
							Please review the errors below and re-upload the correct document to get your visas
						</p>
					</div>

					<div>
						<p className="text-base font-semibold text-gray-900 mb-3">Errors Found</p>
						<div className="flex flex-wrap gap-2">
							{validationResult?.unfixable?.items?.map((item) => (
								<span
									key={item?.validation_name}
									className="flex items-center gap-1.5 text-sm font-medium text-red-600 bg-red-50 px-3 py-1.5 rounded-full border border-red-100">
									<ShieldWarning size={15} weight="fill" />
									{item?.error_text}
								</span>
							))}
						</div>
					</div>

					<div>
						<p className="text-base font-semibold text-gray-900 mb-3">Next Steps</p>
						<div className="flex flex-col gap-4">
							{validationResult?.unfixable?.items?.map((item, index) => (
								<div key={item?.validation_name} className="flex gap-3">
									<div className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-semibold shrink-0 mt-0.5">
										{index + 1}
									</div>
									<p className="text-sm font-semibold text-gray-900">{item?.error_text}</p>
								</div>
							))}
						</div>
					</div>

					<button
						disabled={isDocumentLocked}
						onClick={() => {
							const input = window.document.getElementById(reUploadInputId) as HTMLInputElement;
							input?.click();
						}}
						className={`flex w-full items-center justify-center gap-2 bg-gradient-to-b from-[#E5483B] to-[#B42318] shadow-[0_0_0_1.2px_#B80000] rounded-xl text-sm font-medium px-3 py-3 text-white ${isDocumentLocked ? "opacity-50 cursor-not-allowed" : ""}`}>
						Re-Upload <UploadSimple size={16} className="text-white" />
					</button>
				</div>
			);
		}

		return (
			<div className="w-full overflow-hidden rounded-lg border-[1px] border-green-600 bg-[#EBFAF3] p-4">
				<div className="flex gap-2">
					<Check size={18} weight="bold" className="min-w-4 text-green-600" />
					<div className="flex flex-col gap-4">
						<div className="flex flex-col gap-1">
							<p className="text-green-600 font-semibold text-[13px]">
								Everything seems perfect! Document Verified
							</p>
						</div>
						{document?.question?.category === "Passport Front" && (
							<button className="flex items-center text-left gap-2 rounded-md text-xs bg-gradient-to-r from-green-700 to-green-600 px-3 py-2 text-white w-fit cursor-default">
								<Alarm size={16} className="text-white" />
								Saved {timeSaved / 60} Mins of Form filling
							</button>
						)}
					</div>
				</div>
			</div>
		);
	};

	const renderDesktop = () => {
		if (validationResult?.status === VALIDATION_STATUS.FIXABLE) {
			const fixableType = getFixTypeFromFixableItems(validationResult.fixable.items);

			if (fixableType === FIX_TYPE.AUTO) {
				return (
					<div className="w-full overflow-hidden rounded-lg border-[1px] border-amber-500 bg-[#FEF0C7] p-4">
						<div className="flex gap-2">
							<MagicWand size={18} weight="bold" className="min-w-4 text-amber-600" />
							<div className="flex flex-col gap-4">
								<div className="flex flex-col gap-1">
									<p className="text-amber-800 font-semibold text-[13px]">Fixable issues found</p>
									<ul className="list-disc text-amber-700 text-[13px]">
										{validationResult.fixable.items.map((item) => (
											<li key={item.validation_name}>{item.error_text}</li>
										))}
									</ul>
								</div>
								{answerId && (
									<button
										type="button"
										disabled={isFixing || isDocumentLocked}
										onClick={async () => {
											setIsFixing(true);
											if (!applicationId || !answerId) {
												toast.error("Missing application or answer ID. Cannot apply auto-fix.");
												return;
											}
											try {
												await applyAutoFix(applicationId, answerId);
											} catch (error) {
												toast.error(
													error instanceof AxiosError
														? error.response?.data?.message
														: "Failed to fix document. Please try again."
												);
											} finally {
												setIsFixing(false);
											}
										}}
										className="flex items-center text-left gap-2 rounded-md text-xs bg-gradient-to-t from-amber-700 to-amber-600 border border-[#BC5800] px-8 py-2 text-white w-fit disabled:opacity-50 cursor-pointer">
										{isFixing ? "Fixing..." : "Auto Fix"}
										<Sparkle size={16} className="text-white" />
									</button>
								)}
							</div>
						</div>
					</div>
				);
			}

			return (
				<div className="w-full overflow-hidden rounded-lg border-[1px] border-green-600 bg-[#EBFAF3] p-4">
					<div className="flex gap-2">
						<MagicWand size={18} weight="bold" className="min-w-4 text-green-600" />
						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-1">
								<p className="text-green-600 font-semibold text-[13px]">
									We'll enhance this before submission
								</p>
								<ul className="list-disc text-green-600 text-[13px]">
									{validationResult.fixable.items.map((item) => (
										<li key={item.validation_name}>{item.error_text}</li>
									))}
								</ul>
							</div>
							<button
								type="button"
								className="flex items-center text-left gap-2 rounded-md text-xs bg-gradient-to-r from-green-700 to-green-600 px-3 py-2 text-white w-fit cursor-default">
								<Alarm size={16} className="text-white" />
								Saved {validationResult.fixable.timeSaved} Mins
							</button>
						</div>
					</div>
				</div>
			);
		}

		if (validationResult?.status === VALIDATION_STATUS.UNFIXABLE) {
			return (
				<div className="h-full flex flex-col justify-center">
					<div className="flex gap-2">
						<WarningCircle size={18} weight="bold" className="min-w-4 text-red-600" />
						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-1">
								<p className="text-red-600 font-semibold text-[13px]">Cannot be processed</p>
								<ul className="list-disc text-red-600 text-[13px]">
									{validationResult?.unfixable?.items?.map((item) => (
										<li key={item.validation_name}>{item.error_text}</li>
									))}
								</ul>
							</div>
							<button
								disabled={isDocumentLocked}
								onClick={() => {
									const input = window.document.getElementById(reUploadInputId) as HTMLInputElement;
									input?.click();
								}}
								type="button"
								className={`flex items-center text-left gap-2 rounded-md text-xs bg-gradient-to-t from-red-700 to-red-600 border border-[#B80000] px-8 py-2 text-white w-fit ${isDocumentLocked ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed" : ""}`}>
								Re-Upload <UploadSimple size={16} className="text-white" />
							</button>
						</div>
					</div>
				</div>
			);
		}

		return (
			<div className="w-full overflow-hidden rounded-lg border-[1px] border-green-600 bg-[#EBFAF3] p-4">
				<div className="flex gap-2">
					<Check size={18} weight="bold" className="min-w-4 text-green-600" />
					<div className="flex flex-col gap-4">
						<div className="flex flex-col gap-1">
							<p className="text-green-600 font-semibold text-[13px]">
								Everything seems perfect! Document Verified
							</p>
						</div>
						{document?.question?.category === "Passport Front" && (
							<button
								type="button"
								className="flex items-center text-left gap-2 rounded-md text-xs bg-gradient-to-r from-green-700 to-green-600 px-3 py-2 text-white w-fit cursor-default">
								<Alarm size={16} className="text-white" />
								Saved {timeSaved / 60} Mins of Form filling
							</button>
						)}
					</div>
				</div>
			</div>
		);
	};

	return isMobile ? renderMobile() : renderDesktop();
};

export default DocumentVerificationStatus;
